
import { Component, Vue } from 'vue-property-decorator';
import Course from './types/Course';
import Queue from './types/Queue';
import { shouldShowCourses } from '@/util/SidebarVisibility';
import { DialogProgrammatic as Dialog } from 'buefy';

import { library } from '@fortawesome/fontawesome-svg-core';
import {
	faSignInAlt,
	faSignOutAlt,
	faUserShield,
	faStar as solidStar,
	faUserGraduate,
	faAngleLeft,
	faAngleRight,
	faChartLine,
} from '@fortawesome/free-solid-svg-icons';
import { faStar as regularStar } from '@fortawesome/free-regular-svg-icons';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import ErrorDialog from './util/ErrorDialog';
import EscapeHTML from './util/Sanitization';

library.add(
	faSignInAlt,
	faSignOutAlt,
	faUserShield,
	faGithub,
	solidStar,
	regularStar,
	faUserGraduate,
	faAngleLeft,
	faAngleRight,
	faChartLine
);

@Component
export default class App extends Vue {
	fetchedCourses = false;
	studentView = false;

	created() {
		if ('Notification' in window) {
			Notification.requestPermission();
		}

		// Stop jitter on page load
		this.$root.$data.showCourses = shouldShowCourses(
			this,
			this.$root.$data.queues[this.$route.params.qid]
		);

		this.restart();
	}

	get loginUrl() {
		return process.env.BASE_URL + 'api/oauth2login';
	}

	get logoutUrl() {
		return process.env.BASE_URL + 'api/logout';
	}

	get courses() {
		return Object.values(this.$root.$data.courses)
			.filter((c: Course) => c.queues.length > 0)
			.sort((a: Course, b: Course) => {
				if (a.favorite !== b.favorite) {
					return a.favorite ? -1 : 1;
				}
				return a.shortName < b.shortName ? -1 : 1;
			});
	}

	get footerHtml() {
		return process.env.VUE_APP_FOOTER_HTML;
	}

	get admin() {
		return (
			!this.studentView &&
			this.$root.$data.loggedIn &&
			(this.$root.$data.userInfo.site_admin ||
				this.$root.$data.userInfo.admin_courses.length > 0)
		);
	}

	get siteAdmin() {
		return (
			!this.studentView &&
			this.$root.$data.loggedIn &&
			this.$root.$data.userInfo.site_admin
		);
	}

	goToQueue(q: Queue) {
		this.$root.$data.showCourses = shouldShowCourses(this, q);
		this.$router.push('/queues/' + q.id);
	}

	setStudentView(studentView: boolean) {
		this.studentView = studentView;
		this.$root.$data.studentView = studentView;
	}

	// Drop all courses and authorization information and re-start
	// the loading process. This is essentially a complete refresh
	// without actually refreshing the page.
	restart() {
		this.fetchedCourses = false;
		Vue.set(this.$root.$data, 'courses', {});
		Vue.set(this.$root.$data, 'queues', {});

		Vue.set(this.$root.$data, 'userInfoLoaded', false);
		Vue.set(this.$root.$data, 'loggedIn', false);
		Vue.set(this.$root.$data, 'userInfo', {});

		fetch(process.env.BASE_URL + 'api/courses')
			.then((resp) => {
				if (!resp.ok) return Promise.reject(resp);
				return resp.json();
			})
			.then((data) => {
				data.map((c: any) => {
					const course = new Course(c);
					course.favorite =
						localStorage.getItem('favoriteCourses-' + course.id) !== null;
					Vue.set(this.$root.$data.courses, c.id, course);
					for (const q of course.queues) {
						Vue.set(this.$root.$data.queues, q.id, q);
					}
				});
				this.fetchedCourses = true;
			})
			.catch((error) => {
				this.fetchedCourses = true;

				if (error instanceof Response) {
					return ErrorDialog(error);
				}

				Dialog.alert({
					title: 'Request Failed',
					message: `Failed to load courses. Please try again later. Error: ${EscapeHTML(
						error
					)}`,
					type: 'is-danger',
					hasIcon: true,
				});
			});

		fetch(process.env.BASE_URL + 'api/users/@me')
			.then((resp) => {
				if (resp.status !== 200) {
					return Promise.reject('not logged in');
				}
				return resp.json();
			})
			.then((data) => {
				Vue.set(this.$root.$data, 'userInfoLoaded', true);
				Vue.set(this.$root.$data, 'loggedIn', true);
				Vue.set(this.$root.$data, 'userInfo', data);

				// Check if there's a pending redirect after login
				const redirectPath = localStorage.getItem('loginRedirect');
				if (redirectPath) {
					localStorage.removeItem('loginRedirect');
					this.$router.push(redirectPath);
				}
			})
			.catch(() => (this.$root.$data.userInfoLoaded = true));
	}

	toggleFavorite(c: Course) {
		const original = c.favorite;
		if (original) {
			localStorage.removeItem('favoriteCourses-' + c.id);
		} else {
			localStorage.setItem('favoriteCourses-' + c.id, 'favorite');
		}
		c.favorite = !original;
	}
}
